import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Label } from '@progress/kendo-react-labels';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { ColorPicker, Input, Slider } from '@progress/kendo-react-inputs';

import { UploadReelsResource } from '../resources';
import { useReels } from '../../../../../contexts/reels';
import { rgbaToHex } from '../../../../../utils/hex-to-rgb';
import { GameDocumentContext } from '../../../../../contexts/game-document';
import { ResourceGetById } from '../../../../../utils/game-document/resources';

const qualityData = [
  { value: 'preview', label: 'Preview (360)' },
  { value: 'medium', label: 'Medium (720p)' },
  { value: 'high', label: 'High (1080p)' }
];

const transitionData = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'fadeblack', label: 'Fade Black' },
  { value: 'fadewhite', label: 'Fade White' },
  { value: 'wipeleft', label: 'Wipe Left' },
  { value: 'wiperight', label: 'Wipe Right' },
  { value: 'dissolve', label: 'Dissolve' },
  { value: 'slideleft', label: 'Slide Left' },
  { value: 'slideright', label: 'Slide Right' }
];

const watermarkPosition = [
  { value: 'top_left', label: 'Top-left corner' },
  { value: 'top_right', label: 'Top-right corner' },
  { value: 'bottom_left', label: 'Bottom-left corner' },
  { value: 'bottom_right', label: 'Bottom-right corner' }
];

const watermarkSize = [
  { value: 'extra_small', label: 'Extra Small' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra_large', label: 'Extra Large' }
];

export const VideoSettings = () => {
  const [gameDocument] = React.useContext(GameDocumentContext);

  const { global_settings, handleChange } = useReels();

  const watermarkTypeValue = React.useMemo(() => {
    if (global_settings.watermark === undefined) return 'No Watermark';
    if (
      ['Logo Watermark', 'catalyst-logo', 'game-logo'].includes(
        global_settings.watermark.id
      )
    )
      return 'Logo Watermark';

    return 'Custom Upload';
  }, [global_settings.watermark?.id]);

  const logoUrl = React.useMemo(() => {
    const logoResId = gameDocument?.gameDocument?.overview?.logoResId ?? '';
    if (!logoResId) return '';

    const logo: string =
      ResourceGetById(
        gameDocument?.gameDocument?.resources!,
        gameDocument?.gameDocument?.overview?.logoResId
      )?.value ?? '';

    return logo;
  }, [gameDocument]);

  return (
    <div className={'video-settings'}>
      <b
        style={{
          fontSize: '18px'
        }}>
        Video Settings
      </b>

      <div>
        <Label>Quality</Label>
        <DropDownList
          value={qualityData.find((q) => q.value === global_settings.quality)}
          textField={'label'}
          dataItemKey={'value'}
          fillMode={'outline'}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                quality: e.value.value
              }
            })
          }
          data={qualityData}
        />
      </div>

      <div>
        <Label>Transition</Label>
        <DropDownList
          value={transitionData.find(
            (t) => t.value === global_settings.transition.type
          )}
          fillMode={'outline'}
          textField={'label'}
          dataItemKey={'value'}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                transition: {
                  ...global_settings.transition,
                  type: e.value.value
                }
              }
            })
          }
          data={transitionData}
        />
      </div>

      <div>
        <Label>Transition Duration (Seconds)</Label>
        <Input
          min={0}
          step={0.1}
          type={'number'}
          value={global_settings.transition.duration_secs}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                transition: {
                  ...global_settings.transition,
                  duration_secs: Number(e.value)
                }
              }
            })
          }
        />
      </div>

      <div>
        <Label>Default Image Duration (Seconds)</Label>
        <Input
          type={'number'}
          min={0}
          step={1}
          value={global_settings.transition.default_image_duration_secs}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                transition: {
                  ...global_settings.transition,
                  default_image_duration_secs: Number(e.value)
                }
              }
            })
          }
        />
      </div>

      <div className={'d-flex flex-column gap-1'}>
        <Label>Default Text Slide Background</Label>
        <ColorPicker
          views={['gradient']}
          gradientSettings={{
            opacity: false,
            _adaptive: false
          }}
          flatColorPickerSettings={{
            activeView: 'gradient',
            format: 'hex'
          }}
          fillMode={'outline'}
          value={global_settings.background_color ?? ''}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                background_color: rgbaToHex(e.value)
              }
            })
          }
        />
        <span
          style={{
            fontSize: '14px'
          }}>
          Default background color for text-only slides
        </span>
      </div>

      <hr />

      <div>
        <Label>Watermark Type</Label>
        <DropDownList
          value={watermarkTypeValue}
          fillMode={'outline'}
          onChange={(e) => {
            const settings = cloneDeep(global_settings);
            if (e.value === 'No Watermark') {
              delete settings.watermark;
              handleChange({
                global_settings: settings
              });
              return;
            }

            settings.watermark = {
              id: e.value,
              opacity: '0.5',
              position: 'top_left',
              size: 'medium',
              url: ''
            };
            handleChange({ global_settings: settings });
          }}
          data={['No Watermark', 'Logo Watermark', 'Custom Upload']}
        />
      </div>

      {watermarkTypeValue === 'Logo Watermark' && (
        <>
          <div className={'reels-text-slides-grid-container'}>
            <div className={'d-flex flex-column gap-2'}>
              <span>Catalyst Global logo</span>
              <div
                className={'reels-tasks-media-card'}
                style={{
                  backgroundColor: 'transparent'
                }}
                onClick={() => {
                  const settings = cloneDeep(global_settings);
                  if (settings.watermark) {
                    settings.watermark.id = 'catalyst-logo';
                    settings.watermark.url =
                      'https://stsharedappstorage.blob.core.windows.net/cdn/resources/cg-logo-globe.png';
                  }
                  handleChange({ global_settings: settings });
                }}>
                <img
                  src={
                    'https://stsharedappstorage.blob.core.windows.net/cdn/resources/cg-logo-globe.png'
                  }
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />

                {global_settings.watermark?.id === 'catalyst-logo' && (
                  <div
                    className={
                      'd-flex align-items-center justify-content-center p-1 position-absolute'
                    }
                    style={{
                      top: '8px',
                      left: '8px',
                      fontSize: '12px',
                      borderRadius: '4px',
                      backgroundColor: '#4698CB'
                    }}>
                    Selected
                  </div>
                )}
              </div>
            </div>

            {logoUrl !== '' && (
              <div className={'d-flex flex-column gap-2'}>
                <span>Your game logo</span>
                <div
                  className={'reels-tasks-media-card'}
                  style={{
                    backgroundColor: 'transparent'
                  }}
                  onClick={() => {
                    const settings = cloneDeep(global_settings);
                    if (settings.watermark) {
                      settings.watermark.id = 'game-logo';
                      settings.watermark.url = logoUrl;
                    }
                    handleChange({ global_settings: settings });
                  }}>
                  <img
                    src={logoUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />

                  {global_settings.watermark?.id === 'game-logo' && (
                    <div
                      className={
                        'd-flex align-items-center justify-content-center p-1 position-absolute'
                      }
                      style={{
                        top: '8px',
                        left: '8px',
                        fontSize: '12px',
                        borderRadius: '4px',
                        backgroundColor: '#4698CB'
                      }}>
                      Selected
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {watermarkTypeValue === 'Custom Upload' && (
        <UploadReelsResource allowMultipleUpload={false} selectWhenDone>
          {() => {
            if (!global_settings.watermark?.url) return;

            return (
              <div
                className={'reels-text-slides-grid-container'}
                style={{
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                <img
                  src={global_settings.watermark.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            );
          }}
        </UploadReelsResource>
      )}

      {global_settings.watermark !== undefined && (
        <div className={'watermark-config-wrapper'}>
          <div>
            <Label>Position</Label>
            <DropDownList
              value={watermarkPosition.find(
                (w) => w.value === global_settings.watermark?.position
              )}
              fillMode={'outline'}
              dataItemKey={'value'}
              textField={'label'}
              data={watermarkPosition}
              onChange={(e) =>
                handleChange({
                  global_settings: {
                    ...global_settings,
                    watermark: {
                      ...global_settings.watermark!,
                      position: e.value.value
                    }
                  }
                })
              }
            />
          </div>

          <div>
            <Label>Size</Label>
            <DropDownList
              value={watermarkSize.find(
                (w) => w.value === global_settings.watermark?.size
              )}
              fillMode={'outline'}
              textField={'label'}
              dataItemKey={'value'}
              data={watermarkSize}
              onChange={(e) =>
                handleChange({
                  global_settings: {
                    ...global_settings,
                    watermark: {
                      ...global_settings.watermark!,
                      size: e.value.value
                    }
                  }
                })
              }
            />
          </div>

          <div className={'d-flex flex-column'}>
            <Label>Opacity</Label>
            <Slider
              min={0}
              max={1}
              className={'w-100'}
              step={0.05}
              value={parseFloat(global_settings.watermark.opacity)}
              onChange={(e) => {
                const value = String(e.value.toFixed(2));
                handleChange({
                  global_settings: {
                    ...global_settings,
                    watermark: {
                      ...global_settings.watermark!,
                      opacity: value
                    }
                  }
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
